import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { AromaSampling } from '@/lib/api/aroma-samplings-api'
import type { AromaSamplingFormValues } from '@/lib/schemas/aroma-sampling-schemas'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useAromaSamplings,
    useCreateAromaSampling,
    useDeleteAromaSampling,
    useUpdateAromaSampling,
} from '@/hooks/use-aroma-samplings-query'
import { GrowerSearchSelect } from '@/components/growers/grower-search-select'
import { useUsers } from '@/hooks/use-user-query'
import { m } from '@/lib/paraglide/messages'
import { createAromaSamplingSchema } from '@/lib/schemas/aroma-sampling-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/aroma-samplings',
)({
    component: AromaSamplingsPage,
})

function AromaSamplingsPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('aroma_samplings.write')
    const [editing, setEditing] = useState<AromaSampling | null>(null)
    const [open, setOpen] = useState(false)

    const { data } = useAromaSamplings({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreateAromaSampling()
    const updateMut = useUpdateAromaSampling(editing?.id ?? '')
    const deleteMut = useDeleteAromaSampling()

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">
                    {m.aroma_sampling_title()}
                </h1>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.aroma_sampling_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.aroma_sampling_date()}</TableHead>
                        <TableHead>{m.aroma_sampling_code()}</TableHead>
                        <TableHead>{m.aroma_sampling_weight()}</TableHead>
                        <TableHead>{m.aroma_sampling_used()}</TableHead>
                        <TableHead>{m.aroma_sampling_implementers()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>
                                {row.sampling_date.slice(0, 10)}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                                {row.code ?? '—'}
                            </TableCell>
                            <TableCell>{row.weight}</TableCell>
                            <TableCell>{row.used}</TableCell>
                            <TableCell>
                                {(row.implementers ?? []).length > 0 ? (
                                    <Badge variant="secondary">
                                        {(row.implementers ?? []).length}
                                    </Badge>
                                ) : (
                                    '—'
                                )}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.aroma_sampling_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing
                                ? m.aroma_sampling_edit()
                                : m.aroma_sampling_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <AromaSamplingForm
                        initial={editing}
                        onSubmit={async (values) => {
                            try {
                                const payload = {
                                    ...values,
                                    grower_id: values.grower_id || null,
                                }
                                if (editing) {
                                    await updateMut.mutateAsync(payload)
                                } else {
                                    await createMut.mutateAsync(payload)
                                }
                                setOpen(false)
                            } catch {
                                // mutation onError surfaces the toast; keep dialog open.
                            }
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function AromaSamplingForm({
    initial,
    onSubmit,
    submitting,
}: {
    initial: AromaSampling | null
    onSubmit: (v: AromaSamplingFormValues) => Promise<void>
    submitting: boolean
}) {
    const schema = createAromaSamplingSchema()
    const { data: usersData } = useUsers({ per_page: 200 })
    const users = usersData?.data ?? []

    const form = useForm<AromaSamplingFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            grower_id: initial?.grower_id ?? '',
            sampling_date: initial?.sampling_date?.slice(0, 10) ?? '',
            code: initial?.code ?? '',
            weight: initial?.weight ?? 0,
            old_weight: initial?.old_weight ?? undefined,
            used: initial?.used ?? 0,
            is_active: initial?.is_active ?? true,
            implementer_ids: initial?.implementers?.map((i) => i.user_id) ?? [],
        },
    })

    const selectedImplementers = form.watch('implementer_ids')
    const toggleImplementer = (id: string, checked: boolean) => {
        const next = checked
            ? [...selectedImplementers, id]
            : selectedImplementers.filter((x) => x !== id)
        form.setValue('implementer_ids', next)
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
                <Label>{m.aroma_sampling_grower()}</Label>
                <GrowerSearchSelect
                    value={form.watch('grower_id') || undefined}
                    onChange={(v) => form.setValue('grower_id', v)}
                    fallbackLabel={initial?.grower_name}
                    allowClear
                />
            </div>
            <div>
                <Label>{m.aroma_sampling_date()}</Label>
                <Input type="date" {...form.register('sampling_date')} />
                {form.formState.errors.sampling_date && (
                    <p className="text-xs text-red-500">
                        {form.formState.errors.sampling_date.message}
                    </p>
                )}
            </div>
            <div>
                <Label>{m.aroma_sampling_code()}</Label>
                <Input {...form.register('code')} />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                    <Label>{m.aroma_sampling_weight()}</Label>
                    <Input
                        type="number"
                        step="0.01"
                        {...form.register('weight')}
                    />
                </div>
                <div>
                    <Label>{m.aroma_sampling_old_weight()}</Label>
                    <Input
                        type="number"
                        step="0.01"
                        {...form.register('old_weight')}
                    />
                </div>
                <div>
                    <Label>{m.aroma_sampling_used()}</Label>
                    <Input
                        type="number"
                        step="0.01"
                        {...form.register('used')}
                    />
                </div>
            </div>
            <div>
                <Label>{m.aroma_sampling_implementers()}</Label>
                <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border p-2">
                    {users.map((u) => (
                        <label
                            key={u.id}
                            className="flex items-center gap-2 text-sm"
                        >
                            <input
                                type="checkbox"
                                checked={selectedImplementers.includes(u.id)}
                                onChange={(e) =>
                                    toggleImplementer(u.id, e.target.checked)
                                }
                            />
                            {u.full_name}
                        </label>
                    ))}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Switch
                    checked={form.watch('is_active')}
                    onCheckedChange={(v) => form.setValue('is_active', v)}
                />
                <Label>{m.aroma_sampling_active()}</Label>
            </div>
            <DialogFooter>
                <Button type="submit" disabled={submitting}>
                    {submitting ? '...' : m.aroma_sampling_save()}
                </Button>
            </DialogFooter>
        </form>
    )
}
